/**
 * AB Test Reducer
 * NgRx reducer for managing AB test state
 * 
 * Note: Variant assignments are stored in NgRx state (synced with localStorage)
 * but never sent to backend. Assignments are calculated client-side based on
 * userId + testId hash.
 */

import { createReducer, on } from '@ngrx/store';
import { ABTestDefinition, VariantAssignment } from '../interfaces/models';
import { ABTestConfigActions, VariantAssignmentActions } from './ab-test.actions';

export interface ABTestState {
  assignments: Record<string, VariantAssignment>; // testId -> assignment (client-side only)
  activeTests: ABTestDefinition[];
  loading: boolean;
  error: string | null;
}

export const initialState: ABTestState = {
  assignments: {},
  activeTests: [],
  loading: false,
  error: null,
};

export const abTestReducer = createReducer(
  initialState,

  // Variant Assignment Actions (client-side only, no backend sync)
  on(VariantAssignmentActions.loadAssignmentsSuccess, (state, { assignments }) => {
    const assignmentsMap = assignments.reduce((acc, assignment) => {
      acc[assignment.testId] = assignment;
      return acc;
    }, {} as Record<string, VariantAssignment>);
    return {
      ...state,
      assignments: assignmentsMap,
    };
  }),
  on(VariantAssignmentActions.createAssignmentSuccess, (state, { assignment }) => ({
    ...state,
    assignments: {
      ...state.assignments,
      [assignment.testId]: assignment,
    },
  })),
  on(VariantAssignmentActions.updateAssignment, (state, { assignment }) => ({
    ...state,
    assignments: {
      ...state.assignments,
      [assignment.testId]: assignment,
    },
  })),
  on(VariantAssignmentActions.deleteAssignment, (state, { testId }) => {
    const { [testId]: deleted, ...remainingAssignments } = state.assignments;
    return {
      ...state,
      assignments: remainingAssignments,
    };
  }),

  // AB Test Configuration Actions - Load active test definitions from backend
  on(ABTestConfigActions.loadActiveTests, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ABTestConfigActions.loadActiveTestsSuccess, (state, { tests }) => ({
    ...state,
    activeTests: tests,
    loading: false,
    error: null,
  })),
  on(ABTestConfigActions.loadActiveTestsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  }))
);

export const abTestFeature = {
  NAME: 'abTest',
  reducer: abTestReducer,
  initialState,
};
